import { Clear, Refresh } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ReservationAud,
  ReservationAudDetailsPaymentMade,
  ReservationAudDetailsUpdated,
} from "shared/reservationAud"
import { ReservationAudEventEnum } from "shared/reservationAudData"
import { OsReservationAuditTrailDetailsEmailSent } from "./OsReservationAuditTrailDetailsEmailSent"
import { OsReservationAuditTrailDetailsPaymentMade } from "./OsReservationAuditTrailDetailsPaymentMade"
import { OsReservationAuditTrailDetailsSmsDeliveryUpdated } from "./OsReservationAuditTrailDetailsSmsDeliveryUpdated"
import { OsReservationAuditTrailDetailsSmsSent } from "./OsReservationAuditTrailDetailsSmsSent"
import { OsReservationAuditTrailDetailsUpdated } from "./OsReservationAuditTrailDetailsUpdated"

type OsReservationAuditTrailListProps = {
  data: ReservationAud[]
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  /**
   * The reservation whose trail this is. Rows carrying a DIFFERENT
   * `reservationId` came from a pre-migration ancestor via the backend
   * read-through (DEV-193) and are marked as such — the events are real, they
   * just did not happen on this reservation. Optional so the component still
   * renders unmarked if a caller omits it.
   */
  currentReservationId?: string
}
export const OsReservationAuditTrailList: FC<
  OsReservationAuditTrailListProps
> = ({ data = [], isLoading, isFetching, refetch, currentReservationId }) => {
  const { t } = useTranslation()
  const [selectedFilters, setSelectedFilters] = useState<string[]>([])
  const filterOptions = ["Sms", "Email"]

  // filter data based on selected filters
  const filteredData = useMemo(() => {
    if (selectedFilters.length === 0) return data
    return data.filter((ra) => {
      if (
        selectedFilters.includes("Sms") &&
        (ra.event === ReservationAudEventEnum.smsSent ||
          ra.event === ReservationAudEventEnum.smsDeliveryUpdated)
      )
        return true
      if (
        selectedFilters.includes("Email") &&
        ra.event === ReservationAudEventEnum.emailSent
      )
        return true
      return false
    })
  }, [data, selectedFilters])

  // sort createdAt desc
  const list = useMemo(() => {
    return filteredData?.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [filteredData])

  return (
    <Paper variant="outlined" sx={{ p: 1, pb: 2 }}>
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <Stack direction="column" spacing={1}>
            <Typography variant="body1">{t("auditTrail")}</Typography>
            <Typography variant="caption">{`${filteredData?.length} ${t(
              "events",
            )}`}</Typography>
          </Stack>
          <IconButton
            size="small"
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
          >
            {isLoading || isFetching ? <CircularProgress /> : <Refresh />}
          </IconButton>
        </Stack>
        <Stack
          direction="column"
          spacing={1}
          bgcolor={"#f5f5f5"}
          p={1}
          borderRadius={1}
        >
          <Typography variant="body2">{t("filterByEventType")}</Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1}>
              {filterOptions.map((option) => (
                <Chip
                  key={option}
                  label={t(option)}
                  variant={
                    selectedFilters.includes(option) ? "filled" : "outlined"
                  }
                  color="primary"
                  onClick={() => {
                    setSelectedFilters((prev) =>
                      prev.includes(option)
                        ? prev.filter((f) => f !== option)
                        : [...prev, option],
                    )
                  }}
                />
              ))}
            </Stack>
            {/* clear icon btn */}
            {selectedFilters.length > 0 && (
              <IconButton
                color="primary"
                onClick={() => setSelectedFilters([])}
              >
                <Clear />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="column" spacing={0} pt={2}>
          {list?.map((ra, index) => {
            // Pre-migration row: belongs to the V1 reservation this one was
            // materialized from. Marked, never silently merged in.
            const isPreMigration =
              !!currentReservationId && ra.reservationId !== currentReservationId
            return (
            <Stack
              key={ra.id}
              direction="row"
              spacing={1}
              alignItems="stretch"
            >
              <Stack
                direction="column"
                spacing={1}
                alignItems="flex-end"
                width="80px"
              >
                <Typography variant="caption">
                  {moment(ra.createdAt).format("YYYY-MM-DD")}
                </Typography>
                <Typography variant="caption">
                  {moment(ra.createdAt).format("hh:mm:ss")}
                </Typography>
              </Stack>

              <Box>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    // Muted slate for pre-migration events so the boundary is
                    // legible at a glance without shouting.
                    backgroundColor: isPreMigration ? "#b0bec5" : "lightblue",
                    border: `1px solid ${
                      isPreMigration ? "#b0bec5" : "lightblue"
                    }`,
                    mt: "3px",
                  }}
                />
                {/* line */}

                {index !== list.length - 1 && list.length !== 0 && (
                  <Box
                    sx={{
                      ml: "6px",
                      width: 0,
                      height: "100%",
                      // borderRadius: "50%",
                      // backgroundColor: "lightblue",
                      borderLeft: "1px solid lightblue",
                      position: "relative",
                    }}
                  />
                )}
              </Box>

              <Stack
                key={ra.id}
                direction="column"
                spacing={1}
                pb={6}
                width={"100%"}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight="bold">
                    {t(ra.event)}
                  </Typography>
                  {ra.employeeCode && (
                    <Typography variant="body2">{`${t("employeeCode")}: ${
                      ra.employeeCode
                    }`}</Typography>
                  )}
                  {isPreMigration && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="default"
                      label={t("beforeMigration")}
                      title={t("beforeMigrationHint")}
                    />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  // alignItems="center"
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <Typography variant="caption">
                    {moment(ra.createdAt).fromNow()}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    // height: "150px",
                    // width: "100%",
                    bgcolor: "#f9f9f9",
                    border: "1px solid #f0f0f0",
                    width: 380,
                    overflow: "auto",
                    maxHeight: 400,
                  }}
                >
                  {ra?.details?.diff && (
                    <OsReservationAuditTrailDetailsUpdated
                      detailsUpdated={
                        ra?.details?.diff as ReservationAudDetailsUpdated
                      }
                    />
                  )}

                  {ra?.event === ReservationAudEventEnum.emailSent && (
                    <OsReservationAuditTrailDetailsEmailSent aud={ra} />
                  )}

                  {ra?.event === ReservationAudEventEnum.smsSent && (
                    <OsReservationAuditTrailDetailsSmsSent aud={ra} />
                  )}
                  {ra?.event === ReservationAudEventEnum.smsDeliveryUpdated && (
                    <OsReservationAuditTrailDetailsSmsDeliveryUpdated
                      aud={ra}
                    />
                  )}
                  {ra?.event === ReservationAudEventEnum.paymentMade && (
                    <OsReservationAuditTrailDetailsPaymentMade
                      details={ra?.details as ReservationAudDetailsPaymentMade}
                    />
                  )}
                  {ra?.event === ReservationAudEventEnum.amountUpdated && (
                    <Stack direction="column" spacing={1} p={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {t("amountUpdated")}
                      </Typography>
                      <Divider />
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {t("oldAmount")}:{" "}
                        </Typography>
                        <Typography variant="body2">
                          {ra?.details?.oldAmount || "-"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {t("newAmount")}:{" "}
                        </Typography>
                        <Typography variant="body2">
                          {ra?.details?.newAmount || "-"}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
